/**
 * Find a raw item by its ID across all tables
 */
export function findRawItemById(rawApiData, itemId) {
  if (!rawApiData) return null;

  const tables = [
    rawApiData.formS_TABLE,
    rawApiData.formS_GEN_DETAILS_TABLE,
    rawApiData.formS_TABS_TABLE,
    rawApiData.miscellaneouS_TABLE,
  ].filter(Boolean);

  for (const table of tables) {
    const item = table.find((item) => item.itemid === itemId);
    if (item) {
      const sourceTable = getSourceTableName(rawApiData, table);
      return { ...item, sourceTable };
    }
  }

  return null;
}

/**
 * Get all ancestors of an item (including the item itself)
 */
export function getAncestors(rawApiData, itemId) {
  const ancestors = [];
  const rawItem = findRawItemById(rawApiData, itemId);

  if (!rawItem) {
    console.error(`No raw item found for ID: ${itemId}`);
    return ancestors;
  }

  // Add the item itself
  ancestors.push(rawItem);

  // Follow parent chain
  let currentItem = rawItem;
  while (currentItem?.parentitemid != null) {
    const parentItem = findRawItemById(rawApiData, currentItem.parentitemid);
    if (parentItem) {
      ancestors.unshift(parentItem); // Add parents at the beginning
      currentItem = parentItem;
    } else {
      break;
    }
  }

  return ancestors;
}

/**
 * Get all descendants of an item recursively
 */
export function getAllDescendants(rawApiData, itemId) {
  const descendants = [];

  const findChildren = (parentId) => {
    const tables = [
      rawApiData.formS_TABLE,
      rawApiData.formS_GEN_DETAILS_TABLE,
      rawApiData.formS_TABS_TABLE,
      rawApiData.miscellaneouS_TABLE,
    ].filter(Boolean);

    tables.forEach((table) => {
      const children = table.filter((item) => item.parentitemid === parentId);
      children.forEach((child) => {
        descendants.push(child);
        findChildren(child.itemid); // Recursive call
      });
    });
  };

  findChildren(itemId);
  return descendants;
}

/**
 * Get the original permission value from raw API data
 */
export function getOriginalPermissionValue(rawApiData, itemId, permissionType) {
  // Handle string IDs from Forms Content
  const numericItemId = parseItemId(itemId);
  const rawItem = findRawItemById(rawApiData, numericItemId);

  if (!rawItem) {
    // Check if it's a category
    const categoryItem = rawApiData?.formS_TABLE?.find(
      (item) => item.itemid === numericItemId && item.parentitemid === null
    );
    if (categoryItem) {
      return mapCategoryPermission(categoryItem, permissionType);
    }
    return false;
  }

  // CRITICAL FIX: For Forms Content form items, use Forms table mapping
  // Check if this is a form item (has alldetailsformid) and we're looking for view/edit/print
  if (
    rawItem.alldetailsformid &&
    ["view", "edit", "print"].includes(permissionType)
  ) {
    // This is a form item - map using Forms table permissions
    const permissionMap = {
      view: rawItem.caN_SEARCH === 1,
      edit: rawItem.caN_EDIT === 1,
      print: rawItem.canprint === 1,
    };
    return permissionMap[permissionType] || false;
  }

  // Otherwise use the standard mapping
  return mapItemPermission(rawItem, permissionType);
}

/**
 * Parse item ID from various formats
 */
function parseItemId(itemId) {
  if (typeof itemId === "string") {
    if (itemId.startsWith("form_")) {
      const parts = itemId.split("_");
      return parseInt(parts[1]);
    } else if (itemId.startsWith("parent_")) {
      return parseInt(itemId.replace("parent_", ""));
    } else if (!isNaN(itemId)) {
      return parseInt(itemId);
    }
  }
  return itemId;
}

/**
 * Map permission type to raw data field for categories
 */
function mapCategoryPermission(categoryItem, permissionType) {
  switch (permissionType) {
    case "search_view":
    case "view_open":
    case "create_view":
    case "view":
    case "enabled":
      return categoryItem.view === 1;
    case "create":
      return categoryItem.crt === 1;
    case "edit":
      return categoryItem.edt === 1;
    case "delete":
      return categoryItem.dlt === 1;
    case "print":
      return categoryItem.print === 1;
    default:
      return false;
  }
}

/**
 * Map permission type to raw data field for items
 */
function mapItemPermission(rawItem, permissionType) {
  switch (permissionType) {
    case "search_view":
    case "view_open":
    case "create_view":
    case "enabled":
      return rawItem.caN_SEARCH === 1;
    case "view":
      return rawItem.canview === 1 || rawItem.view === 1;
    case "create":
      return rawItem.caN_CREATE === 1;
    case "edit":
      return rawItem.caN_EDIT === 1;
    case "delete":
      return rawItem.caN_DELETE === 1;
    case "print":
      return rawItem.canprint === 1;
    default:
      console.warn(`Unknown permission type: ${permissionType}`);
      return false;
  }
}

/**
 * Get source table name for an item
 */
function getSourceTableName(rawApiData, table) {
  if (table === rawApiData.formS_TABLE) return "formS_TABLE";
  if (table === rawApiData.formS_GEN_DETAILS_TABLE)
    return "formS_GEN_DETAILS_TABLE";
  if (table === rawApiData.formS_TABS_TABLE) return "formS_TABS_TABLE";
  if (table === rawApiData.miscellaneouS_TABLE) return "miscellaneouS_TABLE";
  return "OTHER";
}
